import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoodIcon from "@mui/icons-material/Mood";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContactApiService from "../services/ContactApiService";
import ConversationService from "../services/ConversationService";
import MessageTemplateService from "../services/MessageTemplateService";

const CHAT_WALLPAPER = {
  backgroundColor: "#efeae2",
  backgroundImage:
    "radial-gradient(rgba(255,255,255,0.34) 1px, transparent 1px), radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)",
  backgroundPosition: "0 0, 18px 18px",
  backgroundSize: "36px 36px",
};

function formatMessageTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getAvatarLabel(name = "") {
  return String(name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";
}

function getSafeLabel(value, fallback = "") {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return value.label || value.title || value.name || value.value || fallback;
  }
  return fallback;
}

/** Must match server `SIM_RESOLVED_TEMPLATE_ENVELOPE` in conversation-service.js */
const SIM_RESOLVED_TEMPLATE_ENVELOPE = "__wSim";

function parseSimulationResolvedTemplateEnvelope(context) {
  if (context == null || typeof context !== "string") return null;
  const t = context.trim();
  if (!t.startsWith("{")) return null;
  try {
    const o = JSON.parse(t);
    if (o && o[SIM_RESOLVED_TEMPLATE_ENVELOPE] === 1 && typeof o.body === "string") return o;
  } catch (e) {
    /* ignore */
  }
  return null;
}

function flattenInteractiveListRows(list) {
  if (!list) return [];
  const raw = list.list_row || list.rows || [];
  const out = [];
  for (const section of raw) {
    if (section && Array.isArray(section.row)) {
      for (const row of section.row) {
        const id = row.id || row.row_id || row.label_id;
        if (!id) continue;
        out.push({
          id: String(id),
          label: getSafeLabel(row.title || row.name || row.label, "Option"),
          description: row.description ? String(row.description) : "",
        });
      }
    } else if (section && (section.id || section.title || section.list_title)) {
      const id = section.id || section.label_id;
      if (!id) continue;
      out.push({
        id: String(id),
        label: getSafeLabel(section.list_title || section.title || section.name, "Option"),
        description: "",
      });
    }
  }
  return out;
}

function MessageBubble({
  messageId = "",
  content,
  isSender,
  actions = [],
  interactiveList = null,
  onActionClick,
  selectedActions = {},
  submittingActionId = "",
  timestamp = "",
  templateName = "",
  headerMedia = null,
  headerText = "",
  footerText = "",
  ctaButtons = [],
  simulationPayment = null,
  onSimulatePaymentPaid,
}) {
  const [listOpen, setListOpen] = useState(false);

  useEffect(() => {
    setListOpen(false);
  }, [messageId]);

  const listRows = flattenInteractiveListRows(interactiveList);
  const listButtonLabel = getSafeLabel(interactiveList?.buttonText || interactiveList?.button_text, "View options");
  const showListToggle = Boolean(onActionClick) && listRows.length > 0;

  const renderActionButton = (action) => {
    const href = action.href && /^https?:\/\//i.test(String(action.href).trim())
      ? String(action.href).trim()
      : null;
    if (href) {
      return (
        <Button
          key={action.id}
          size="small"
          component="a"
          href={href}
          target="_blank"
          rel="noreferrer"
          variant="outlined"
          sx={{
            textTransform: "none",
            justifyContent: "center",
            borderRadius: 2.5,
            borderColor: "#d1d7db",
            color: "#027eb5",
            backgroundColor: "#fff",
            "&:hover": {
              borderColor: "#aebac1",
              backgroundColor: "#f5f6f6",
            },
          }}
        >
          {getSafeLabel(action.label, "Open link")}
        </Button>
      );
    }
    return (
      <Button
        key={action.id}
        size="small"
        variant={selectedActions[action.id] ? "contained" : "outlined"}
        onClick={() => onActionClick?.(action)}
        disabled={Boolean(selectedActions[action.id]) || submittingActionId === action.id}
        sx={{
          textTransform: "none",
          justifyContent: "flex-start",
          borderRadius: 2.5,
          borderColor: "#d1d7db",
          color: selectedActions[action.id] ? "#fff" : "#027eb5",
          backgroundColor: selectedActions[action.id] ? "#00a884" : "#fff",
          "&:hover": {
            borderColor: "#aebac1",
            backgroundColor: selectedActions[action.id] ? "#00a884" : "#f5f6f6",
          },
        }}
      >
        {selectedActions[action.id]
          ? `Selected: ${getSafeLabel(action.label, "Select")}`
          : getSafeLabel(action.label, "Select")}
      </Button>
    );
  };

  return (
    <Box sx={{ display: "flex", justifyContent: isSender ? "flex-end" : "flex-start", mb: 1.25 }}>
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1.1,
          maxWidth: "82%",
          bgcolor: isSender ? "#d9fdd3" : "#fff",
          borderRadius: isSender ? "15px 15px 4px 15px" : "15px 15px 15px 4px",
          boxShadow: "0 1px 1px rgba(11,20,26,0.13)",
          border: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        {templateName ? (
          <Typography sx={{ fontSize: 12, color: "#667781", fontWeight: 600, mb: 0.5 }}>
            {templateName}
          </Typography>
        ) : null}
        {headerText ? (
          <Typography sx={{ fontSize: 13, color: "#111b21", fontWeight: 700, mb: 0.75 }}>
            {headerText}
          </Typography>
        ) : null}
        {headerMedia?.url && (headerMedia.type === "image" || headerMedia.type === "photo") ? (
          <Box
            component="img"
            src={headerMedia.url}
            alt={templateName || "template media"}
            sx={{
              width: "100%",
              maxHeight: 220,
              objectFit: "cover",
              borderRadius: 2,
              mb: 1,
              display: "block",
            }}
          />
        ) : null}
        {headerMedia?.url && headerMedia.type === "video" ? (
          <Box
            component="video"
            src={headerMedia.url}
            controls
            sx={{
              width: "100%",
              maxHeight: 240,
              borderRadius: 2,
              mb: 1,
              display: "block",
              backgroundColor: "#000",
            }}
          />
        ) : null}
        {headerMedia?.url && headerMedia.type === "document" ? (
          <Box
            sx={{
              mb: 1,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              backgroundColor: "rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111b21" }}>
              Document attachment
            </Typography>
            <Typography
              component="a"
              href={headerMedia.url}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 12, color: "#027eb5", textDecoration: "none" }}
            >
              Open document
            </Typography>
          </Box>
        ) : null}
        <Typography sx={{ fontSize: 14, color: "#111b21", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          {content}
        </Typography>
        {footerText ? (
          <Typography sx={{ fontSize: 12, color: "#667781", mt: 0.75 }}>
            {footerText}
          </Typography>
        ) : null}
        {ctaButtons.length > 0 ? (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {ctaButtons.map((button, index) => {
              const href = (button.value || button.url || button.link || "").trim();
              const safeHref = /^https?:\/\//i.test(href) ? href : null;
              return (
              <Button
                key={`${button.value || button.label || "cta"}_${index}`}
                size="small"
                component={safeHref ? "a" : "button"}
                href={safeHref || undefined}
                target={safeHref ? "_blank" : undefined}
                rel={safeHref ? "noreferrer" : undefined}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  justifyContent: "center",
                  borderRadius: 2.5,
                  borderColor: "#d1d7db",
                  color: "#027eb5",
                  backgroundColor: "#fff",
                  "&:hover": {
                    borderColor: "#aebac1",
                    backgroundColor: "#f5f6f6",
                  },
                }}
              >
                {getSafeLabel(button.label, "Open")}
              </Button>
              );
            })}
          </Stack>
        ) : null}
        {simulationPayment?.show_simulate_paid && onSimulatePaymentPaid ? (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Button
              type="button"
              size="small"
              variant="contained"
              onClick={onSimulatePaymentPaid}
              disabled={submittingActionId === "__payment_paid__"}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "#128C7E",
                "&:hover": { backgroundColor: "#0f7a6e" },
              }}
            >
              Simulate: payment completed (order paid)
            </Button>
            <Typography sx={{ fontSize: 11, color: "#667781", lineHeight: 1.35 }}>
              Sets session payment status to paid and continues your flow (same as Meta order_details success).
            </Typography>
          </Stack>
        ) : null}
        {actions.length > 0 ? (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {actions.map((action) => renderActionButton(action))}
          </Stack>
        ) : null}
        {showListToggle ? (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setListOpen((prev) => !prev)}
              sx={{
                textTransform: "none",
                justifyContent: "center",
                borderRadius: 2.5,
                borderColor: "#d1d7db",
                color: "#027eb5",
                backgroundColor: "#fff",
                "&:hover": {
                  borderColor: "#aebac1",
                  backgroundColor: "#f5f6f6",
                },
              }}
            >
              {listOpen ? "Hide list options" : listButtonLabel}
            </Button>
            {listOpen
              ? listRows.map((row) => (
                <Button
                  key={row.id}
                  size="small"
                  variant={selectedActions[row.id] ? "contained" : "outlined"}
                  onClick={() => onActionClick?.({ id: row.id, label: row.label })}
                  disabled={Boolean(selectedActions[row.id]) || submittingActionId === row.id}
                  sx={{
                    textTransform: "none",
                    justifyContent: "flex-start",
                    borderRadius: 2.5,
                    pl: 1.5,
                    borderColor: "#d1d7db",
                    color: selectedActions[row.id] ? "#fff" : "#027eb5",
                    backgroundColor: selectedActions[row.id] ? "#00a884" : "#fff",
                  }}
                >
                  {row.description
                    ? `${getSafeLabel(row.label, "Option")} — ${row.description}`
                    : getSafeLabel(row.label, "Option")}
                </Button>
              ))
              : null}
          </Stack>
        ) : null}
        <Typography sx={{ textAlign: "right", fontSize: 11, color: "#667781", mt: 0.5 }}>
          {timestamp}
        </Typography>
      </Paper>
    </Box>
  );
}

function ChatPane({
  title,
  subtitle,
  avatarLabel,
  messages,
  messageRef,
  inputValue,
  onInputChange,
  onSend,
  extraComposer,
  actionsForMessage,
  onActionClick,
  onSimulatePaymentPaid,
  selectedActions,
  submittingActionId,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid #d8e0e5",
        backgroundColor: "#f0f2f5",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: "#f0f2f5",
          borderBottom: "1px solid #d8e0e5",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: "#00a884", width: 40, height: 40, fontSize: 14 }}>
            {avatarLabel}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111b21" }}>{title}</Typography>
            <Typography sx={{ fontSize: 12, color: "#667781" }}>{subtitle}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ color: "#54656f" }}>
          <SearchIcon sx={{ fontSize: 20 }} />
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </Stack>
      </Box>

      <Box ref={messageRef} sx={{ ...CHAT_WALLPAPER, flex: 1, overflow: "auto", px: 2, py: 2 }}>
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            messageId={message.id}
            content={message.text}
            isSender={message.isSender}
            actions={actionsForMessage(message)}
            interactiveList={message.fromClient ? message.interactiveList : null}
            onActionClick={onActionClick}
            selectedActions={selectedActions}
            submittingActionId={submittingActionId}
            timestamp={message.timestamp}
            templateName={message.templateName}
            headerMedia={message.headerMedia}
            headerText={message.headerText}
            footerText={message.footerText}
            ctaButtons={message.ctaButtons}
            simulationPayment={message.fromClient ? message.simulationPayment : null}
            onSimulatePaymentPaid={
              message.fromClient && onSimulatePaymentPaid ? onSimulatePaymentPaid : undefined
            }
          />
        ))}
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, backgroundColor: "#f0f2f5", borderTop: "1px solid #d8e0e5" }}>
        {extraComposer}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: "#fff",
              border: "1px solid #d1d7db",
            }}
          >
            <MoodIcon sx={{ color: "#8696a0", fontSize: 22 }} />
            <TextField
              fullWidth
              size="small"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder="Type a message"
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: 14, color: "#111b21" },
              }}
            />
          </Box>
          <Button
            variant="contained"
            onClick={onSend}
            sx={{
              minWidth: 46,
              width: 46,
              height: 46,
              borderRadius: "50%",
              boxShadow: "none",
              backgroundColor: "#00a884",
              "&:hover": {
                backgroundColor: "#029377",
                boxShadow: "none",
              },
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function ChatSimulationView() {
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [messages, setMessages] = useState([]);
  const [clientInput, setClientInput] = useState("");
  const [customerInput, setCustomerInput] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [selectedActions, setSelectedActions] = useState({});
  const [submittingActionId, setSubmittingActionId] = useState("");
  const clientMessagesRef = useRef(null);
  const customerMessagesRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const response = await ContactApiService.getContactsDetails({ page: 1, limit: 200 });
      const templateRows = await MessageTemplateService.getAllTemplates();
      if (mounted) {
        setContacts(response?.data?.contacts || []);
        setTemplates(templateRows || []);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const loadMessages = async (targetConversationId) => {
    if (!targetConversationId) return;
    const response = await ConversationService.getSimulationMessages(targetConversationId);
    const sorted = [...(response?.data || [])].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    setMessages(sorted);
  };

  const handleStart = async () => {
    setError("");
    if (!selectedContactId) {
      setError("Please choose a customer phone number.");
      return;
    }
    try {
      const response = await ConversationService.createSimulationSession(selectedContactId);
      const createdConversationId = response?.data?.conversation_id || response?.conversation_id;
      setConversationId(createdConversationId);
      setSelectedActions({});
      setSubmittingActionId("");
      await loadMessages(createdConversationId);
      setInfoMessage("Simulation started successfully.");
    } catch (e) {
      setError(e?.response?.data?.error?.message || "Unable to create simulation session.");
    }
  };

  const sendSimulated = async (senderType) => {
    if (!conversationId) return;
    const text = senderType === "client" ? clientInput.trim() : customerInput.trim();
    if (!text) return;
    await ConversationService.sendSimulationMessage({
      conversation_id: conversationId,
      contact_id: selectedContactId,
      sender_type: senderType,
      message_type: "text",
      message: text,
    });
    if (senderType === "client") setClientInput("");
    if (senderType === "customer") setCustomerInput("");
    await loadMessages(conversationId);
  };

  const sendClientTemplate = async () => {
    if (!conversationId || !selectedTemplateId) return;
    await ConversationService.sendSimulationMessage({
      conversation_id: conversationId,
      contact_id: selectedContactId,
      sender_type: "client",
      message_type: "text",
      template_id: selectedTemplateId,
      message: "",
    });
    setSelectedTemplateId("");
    await loadMessages(conversationId);
  };

  const mappedMessages = messages.map((m) => {
    const simResolved =
      m.message_order === "send" && m.template_id
        ? parseSimulationResolvedTemplateEnvelope(m.context)
        : null;
    const templateHeaderText =
      typeof m.template?.content?.header === "string"
        ? m.template.content.header
        : m.template?.content?.header?.text || "";
    const templateFooterText = m.template?.content?.footer || "";

    return {
    id: m.id,
    text: (() => {
      const body = m.template?.content?.body;
      const bodyStr = typeof body === "string" ? body.trim() : "";
      const ctxStr =
        m.context == null ? "" : typeof m.context === "string" ? m.context.trim() : "";
      // Outbound simulation: resolved copy in `context` (JSON envelope or legacy plain string).
      if (m.message_order === "send" && m.template_id && simResolved) {
        return simResolved.body;
      }
      if (m.message_order === "send" && m.template_id && ctxStr) {
        return typeof m.context === "string" ? m.context : String(m.context);
      }
      if (bodyStr) return body;
      if (m.context) return m.context;
      return m.template?.name || "[Template message]";
    })(),
    fromClient: m.message_order === "send",
    timestamp: formatMessageTime(m.created_at),
    templateName: m.template?.name || "",
    headerText: simResolved ? String(simResolved.header ?? "") : templateHeaderText,
    footerText: simResolved ? String(simResolved.footer ?? "") : templateFooterText,
    headerMedia: (() => {
      const header = m.template?.content?.header;
      const mediaUrl = header?.media_url || m.template?.content?.media_url || null;
      const mediaType = header?.media_type || null;
      if (!mediaUrl || !mediaType) return null;
      return {
        url: mediaUrl,
        type: mediaType,
      };
    })(),
    ctaButtons: (() => {
      const buttons = m.template?.content?.buttons || [];
      return buttons
        .map((button) => {
          const t = String(button.type || "").toLowerCase();
          const url = String(button.url || button.link || "").trim()
            || (typeof button.value === "string" && /^https?:\/\//i.test(button.value) ? button.value.trim() : "");
          if (t !== "website" && t !== "url" && !/^https?:\/\//i.test(url)) return null;
          return {
            label: button.name || button.label || button.text || "Open",
            value: url || button.value,
          };
        })
        .filter(Boolean);
    })(),
    actions: (() => {
      const buttons = m.template?.content?.buttons || [];
      return buttons.map((btn, index) => {
        const t = String(btn.type || "").toLowerCase();
        const href = String(btn.url || btn.link || "").trim()
          || (typeof btn.value === "string" && /^https?:\/\//i.test(btn.value) ? btn.value.trim() : "");
        const isUrl = t === "url" || t === "website" || /^https?:\/\//i.test(href);
        const labelFallback = isUrl ? "Open link" : "Select";
        return {
          id: String(btn.id || btn.btn_value || btn.reply?.id || `btn_${m.id}_${index}`),
          label: getSafeLabel(
            btn.name || btn.btn_label || btn.label || btn.reply?.title,
            labelFallback,
          ),
          href: isUrl ? href : null,
        };
      });
    })(),
    interactiveList: (() => {
      const list = m.template?.content?.list;
      if (!list) return null;
      if (flattenInteractiveListRows(list).length === 0) return null;
      return list;
    })(),
    simulationPayment: m.template?.content?.simulation || null,
  };
  });

  const selectedContact = useMemo(
    () => contacts.find((contact) => contact.getId() === selectedContactId) || null,
    [contacts, selectedContactId],
  );

  const clientMessages = mappedMessages.map((message) => ({
    ...message,
    isSender: message.fromClient,
  }));
  const customerMessages = mappedMessages.map((message) => ({
    ...message,
    isSender: !message.fromClient,
  }));

  const handleInteractiveActionClick = async (action) => {
    if (!conversationId || !action?.id) return;
    if (selectedActions[action.id]) return;
    setSubmittingActionId(action.id);
    await ConversationService.sendSimulationMessage({
      conversation_id: conversationId,
      contact_id: selectedContactId,
      sender_type: "customer",
      message_type: "text",
      message: getSafeLabel(action.label, "Selected option"),
      action_id: action.id,
    });
    setSelectedActions((prev) => ({ ...prev, [action.id]: true }));
    setSubmittingActionId("");
    await loadMessages(conversationId);
  };

  const handleSimulatePaymentPaid = async () => {
    if (!conversationId || !selectedContactId) return;
    setSubmittingActionId("__payment_paid__");
    try {
      await ConversationService.sendSimulationMessage({
        conversation_id: conversationId,
        contact_id: selectedContactId,
        sender_type: "customer",
        message_type: "text",
        message: "Payment completed",
        simulation_payment_paid: true,
      });
      await loadMessages(conversationId);
    } finally {
      setSubmittingActionId("");
    }
  };

  useEffect(() => {
    if (clientMessagesRef.current) {
      clientMessagesRef.current.scrollTop = clientMessagesRef.current.scrollHeight;
    }
    if (customerMessagesRef.current) {
      customerMessagesRef.current.scrollTop = customerMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        height: "100%",
        minHeight: { xs: "calc(100vh - 96px)", md: "calc(100vh - 72px)" },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, rgba(232,245,233,0.55) 0%, rgba(247,248,250,1) 30%, rgba(247,248,250,1) 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 4,
          border: "1px solid #d8e0e5",
          backgroundColor: "rgba(255,255,255,0.92)",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#111b21", mb: 0.5 }}>
              Chat Simulation
            </Typography>
            <Typography sx={{ color: "#667781", fontSize: 14 }}>
              Preview both sides in a WhatsApp-style chat without using the live WhatsApp API.
            </Typography>
          </Box>
          <Chip
            label={conversationId ? "Simulation Active" : "Ready to Start"}
            sx={{
              alignSelf: { xs: "flex-start", lg: "center" },
              backgroundColor: conversationId ? "#d9fdd3" : "#f0f2f5",
              color: "#111b21",
              fontWeight: 600,
            }}
          />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="Customer phone number"
            value={selectedContactId}
            onChange={(e) => setSelectedContactId(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: "#fff",
              },
            }}
          >
            {contacts.map((contact) => (
              <MenuItem key={contact.getId()} value={contact.getId()}>
                {contact.getName()} - {contact.getPhoneNumber()}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={handleStart}
            sx={{
              minWidth: 170,
              borderRadius: 3,
              boxShadow: "none",
              backgroundColor: "#00a884",
              "&:hover": {
                backgroundColor: "#029377",
                boxShadow: "none",
              },
            }}
          >
            Start Simulation
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Typography color="error" sx={{ mb: 1.5 }}>
          {error}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          flex: 1,
          minHeight: 0,
          height: "100%",
          maxHeight: "100%",
          alignItems: "stretch",
        }}
      >
        <ChatPane
          title="Client Window"
          subtitle={selectedContact ? `Talking to ${selectedContact.getName()}` : "Choose a customer to begin"}
          avatarLabel="CL"
          messages={clientMessages}
          messageRef={clientMessagesRef}
          inputValue={clientInput}
          onInputChange={setClientInput}
          onSend={() => sendSimulated("client")}
          actionsForMessage={() => []}
          selectedActions={selectedActions}
          submittingActionId={submittingActionId}
          extraComposer={
            <Stack direction="row" spacing={1} sx={{ mb: 1.1 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Template"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    backgroundColor: "#fff",
                  },
                }}
              >
                {templates.map((template) => (
                  <MenuItem key={template.getId()} value={template.getId()}>
                    {template.getTemplateName()}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                onClick={sendClientTemplate}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderColor: "#d1d7db",
                  color: "#111b21",
                }}
              >
                Send Template
              </Button>
            </Stack>
          }
        />

        <ChatPane
          title={selectedContact?.getName()}
          subtitle={selectedContact ? `${selectedContact.getPhoneNumber()}` : "Preview customer experience"}
          avatarLabel={getAvatarLabel(selectedContact?.getName())}
          messages={customerMessages}
          messageRef={customerMessagesRef}
          inputValue={customerInput}
          onInputChange={setCustomerInput}
          onSend={() => sendSimulated("customer")}
          actionsForMessage={(message) => (message.fromClient ? message.actions : [])}
          onActionClick={handleInteractiveActionClick}
          onSimulatePaymentPaid={handleSimulatePaymentPaid}
          selectedActions={selectedActions}
          submittingActionId={submittingActionId}
        />
      </Box>

      <Snackbar
        open={Boolean(infoMessage)}
        autoHideDuration={2500}
        onClose={() => setInfoMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setInfoMessage("")}>
          {infoMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
